import type { OperationFailed, OperationResult } from "@skbkontur/operation-result";
import { createFailure, isSuccess } from "@skbkontur/operation-result";
import type {
  CreateWidgetApi,
  WidgetDependenciesConstraint,
  WidgetFaultConstraint,
  WidgetGenericFault,
} from "../public-types.js";
import { tryExecute, type UnexpectedFault } from "../tryExecute/tryExecute.js";

export const createWidgetApiExportName = "createWidgetApi";

type NoExportFault = WidgetGenericFault<"namespace-no-export">;

const createNoExportFailure = (message: string): OperationFailed<NoExportFault> =>
  createFailure({
    type: "namespace-no-export",
    message,
  });

type ImportWidgetModuleFromNamespaceFaults<TFault> =
  | NoExportFault
  | {
      readonly type: "passthrough";
      readonly inner: TFault | UnexpectedFault;
    };

export type WidgetModuleNamespace<
  TDependencies extends WidgetDependenciesConstraint,
  TImportFault extends WidgetFaultConstraint,
  TApi,
> = {
  readonly [createWidgetApiExportName]: CreateWidgetApi<TDependencies, TImportFault, TApi>;
};

export async function importWidgetModuleFromNamespace<
  TDependencies extends WidgetDependenciesConstraint,
  TImportFault extends WidgetFaultConstraint,
  TApi,
>(
  namespace: WidgetModuleNamespace<TDependencies, TImportFault, TApi>,
  widgetUrl: URL,
  dependencies: TDependencies
): Promise<OperationResult<ImportWidgetModuleFromNamespaceFaults<TImportFault>, TApi>> {
  if (!namespace) {
    return createNoExportFailure(`Module '${widgetUrl}' has no namespace`);
  }

  const createWidgetApi = namespace[createWidgetApiExportName];
  if (!createWidgetApi) {
    return createNoExportFailure(
      `Namespace '${JSON.stringify(namespace)}' doesn't have '${createWidgetApiExportName}' export`
    );
  }

  if (typeof createWidgetApi !== "function") {
    return createNoExportFailure(`Export '${createWidgetApiExportName}' is not a function: '${createWidgetApi}'`);
  }

  const createWidgetApiResult = await tryExecute(() => createWidgetApi({ dependencies }));
  if (isSuccess(createWidgetApiResult)) {
    return createWidgetApiResult;
  }

  return createFailure({
    type: "passthrough",
    inner: createWidgetApiResult.fault,
  });
}
